/**
 * mapMode — store Zustand para alternar entre la vista operativa (MapLibre
 * "Liberty" estilizado con extrusión de edificios, la del operador ARS93) y
 * la vista realista (Google Photorealistic 3D Tiles vía Map3DElement).
 *
 * - Persistencia en localStorage (`scg-map-mode`) para mantener la elección
 *   entre recargas — un admin que entra a hacer una demo no quiere reactivar
 *   el modo cada vez.
 * - Default por entorno: en `dev` arranca en `realistic` para iterar el modo
 *   nuevo sin tener que hacer click cada recarga; en producción arranca en
 *   `liberty` (el operador del día a día no debe ver la vista cara por
 *   defecto).
 *
 * Visibilidad del toggle: el botón sólo se renderiza para usuarios con
 * `is_admin === true` (ver `App.tsx`). Si un viewer cambiase el localStorage
 * a mano, el componente fallback (`RealisticMapView`) seguiría exigiendo la
 * key Google y, sobre todo, no causaría facturación inesperada porque el
 * SDK sólo se carga cuando el componente se monta.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type MapMode = 'liberty' | 'realistic';

interface MapModeState {
  mode: MapMode;
  setMode: (mode: MapMode) => void;
  toggle: () => void;
}

const initialMode: MapMode = import.meta.env.DEV ? 'realistic' : 'liberty';

export const useMapMode = create<MapModeState>()(
  persist(
    (set, get) => ({
      mode: initialMode,
      setMode: (mode) => set({ mode }),
      toggle: () => set({ mode: get().mode === 'liberty' ? 'realistic' : 'liberty' }),
    }),
    {
      name: 'scg-map-mode',
      // si el localStorage tiene un valor inválido (cambio de tipo, manipulación),
      // descartar y volver al default.
      partialize: (s) => ({ mode: s.mode === 'liberty' || s.mode === 'realistic' ? s.mode : initialMode }),
    },
  ),
);
